import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react'
import {
  AuthContextType,
  LoginCredentials,
  RegisterCredentials,
  User,
} from '@/types'
import { authService } from '@/services/auth'

// Create auth context
const AuthContext = createContext<Maybe<AuthContextType>>(undefined)

// Auth provider component
interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Nullable<User>>(null)
  const [token, setToken] = useState<Nullable<string>>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Nullable<string>>(null)

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = () => {
      try {
        setIsLoading(true)

        // Check if user is already authenticated
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser()
          const storedToken = authService.getStoredToken()

          if (storedUser && storedToken) {
            setUser(storedUser)
            setToken(storedToken)
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  // Login function
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      try {
        setIsLoading(true)
        setError(null)
        await authService.login(credentials)

        // After successful login, get user and token from storage
        const storedUser = authService.getStoredUser()
        const storedToken = authService.getStoredToken()

        if (storedUser && storedToken) {
          setUser(storedUser)
          setToken(storedToken)
        } else {
          console.warn('Login - User or token not found in storage')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Login failed'
        setError(errorMessage)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  // Register function
  const register = useCallback(
    async (credentials: RegisterCredentials): Promise<void> => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await authService.register(credentials)

        if ('user' in response && response.user) {
          const userFromResponse = response.user as User
          setUser(userFromResponse)
        }
        if ('accessToken' in response) {
          setToken(response.accessToken as string)
        } else {
          setToken('mock')
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Registration failed'
        setError(errorMessage)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const logout = useCallback(async (): Promise<void> => {
    await authService.logout()

    setUser(null)
    setToken(null)
    setError(null)

    const currentPath = window.location.pathname
    const langMatch = currentPath.match(/^\/(en|zh)/)
    const currentLang = langMatch ? langMatch[1] : 'en'

    window.location.href = `/${currentLang}/login`
  }, [])

  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const newToken = await authService.refreshToken()
      setToken(newToken)
    } catch (err) {
      logout()
      throw err
    }
  }, [logout])

  // Clear error function
  const clearError = useCallback((): void => {
    setError(null)
  }, [])

  // Set authenticated function (called after successful login/OTP verification)
  const setAuthenticated = useCallback(
    (authenticatedUser: User, authToken: string): void => {
      setUser(authenticatedUser)
      setToken(authToken)
      setError(null)
    },
    []
  )

  // Context value
  const contextValue: AuthContextType = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: authService.isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      refreshToken,
      clearError,
      setAuthenticated,
    }),
    [
      user,
      token,
      isLoading,
      error,
      login,
      register,
      logout,
      refreshToken,
      clearError,
      setAuthenticated,
    ]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

// Custom hook to use auth context
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
